import { useCallback, useState } from "react";
import { useAuth } from "~/presentation/core/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { logError } from "~/presentation/shared/utils/errors";
import { LOG_TAG } from "../CustomerPage/hooks/useApprovalModels";
import { customerBiService } from "~/application/usecases/CustomerDashboardBI";
import { CustomerBiContaner } from "./CustomerBiContainer";
import { CustomerBI } from "~/application/types/entities/CustomerBI.type";
import { CustomerBiQuery } from "~/application/usecases/CustomerDashboardBI/CustomerDashboardBIService";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { CostCenter } from "~/application/types";
import { QueryKeys, QueryTimes } from "~/application/constants";
import { costCenterService } from "~/application/usecases";
import { AGENCY_CUSTOMER_ID } from "~/application/constants/storageKeys";
import { useDebounce } from "use-debounce";
import { FilterKeyType, getDateRange, getDateRangeByPeriod } from "./utils";
import dayjs from "dayjs";

const SNACKBAR_MESSAGES = {
  LOAD_ERROR_MESSAGE: "Erro ao buscar dados",
};

export type FilterCustomerBI = {
  periodFrom?: Date | null;
  periodUntil?: Date | null;
  costCenters?: CostCenter[];
};

export type MultiValue = {
  costCenter: string[];
  issuers: string[];
  projects: string[];
  items: string[];
  phases: string[];
  startEndDate: { start: string; end: string } | null;
  units: string[];
  filterKeyType?: string;
  filterRangePeriod: { startDate: string; endDate: string; isPeriodOnBar: boolean } | null;
};

export function CustomerDashboardBI() {
  const { user } = useAuth();
  const filterData = useForm<FilterCustomerBI>();
  const { periodFrom, periodUntil, costCenters } = filterData.watch();
  const [costCenterName, setCostCenterName] = useState("");

  const [activeFilter, setActiveFilter] = useState({ type: "year", value: "12 meses" });

  const handleSetActiveFilter = useCallback(({ type, value }: { type: string; value: string }) => {
    setActiveFilter({ type, value });
  }, []);

  const allItems = ["road", "hotel", "airway", "vehicle"];

  const [deboucedFiltersName] = useDebounce(costCenterName, 700);

  const [modalPrtalIsOpen, setModalPortalIsOpen] = useState(false);

  const customerId = user?.customer?.uuid || (localStorage.getItem(AGENCY_CUSTOMER_ID) as string);

  const defaultByItemQuery = { itemName: "", value: null };

  const [selectedMultiValues, setSelectedMultiValues] = useState<MultiValue>({
    costCenter: [],
    issuers: [],
    projects: [],
    items: [],
    units: [],
    phases: [],
    filterKeyType: FilterKeyType.YEAR,
    filterRangePeriod: {
      startDate: getDateRange(FilterKeyType.YEAR).startDate,
      endDate: getDateRange(FilterKeyType.YEAR).endDate,
      isPeriodOnBar: false,
    },
    startEndDate: null,
  });
  
  const getSelectedItemsState = () => {
    const selectedItem = selectedMultiValues.items.length > 0 ? selectedMultiValues.items[0] : null;
    return allItems.reduce((acc, item) => {
      acc[item] = selectedItem ? item === selectedItem : true;
      return acc;
    }, {} as Record<string, boolean>);
  };

  const isByItem = getSelectedItemsState();

  const handleSelectMultiValues = useCallback(
    ({ value, field }: { value: string; field: keyof typeof selectedMultiValues }) => {
      if (field === "startEndDate" || field === "filterRangePeriod") return;
      setSelectedMultiValues((prevSelected) => {
        const updatedValues = new Set<string>(prevSelected[field] as string[]);

        if (updatedValues.has(value)) {
          return { ...prevSelected, [field]: [] };
        }

        updatedValues.add(value);
        return { ...prevSelected, [field]: Array.from(updatedValues) };
      });
    },
    [setSelectedMultiValues]
  );

  const onRemoveItem = (item: string) => {
    setSelectedMultiValues((prevSelected) => {
      const updatedItems = prevSelected.items.filter((i) => i !== item);
      return { ...prevSelected, items: updatedItems };
    });
  };

  const handleSelectStartDate = (startEndDate: MultiValue["startEndDate"]) => {
    setSelectedMultiValues((prev) => ({ ...prev, startEndDate }));
  };

  const handleSelectPeriodDate = (filterRangePeriod: MultiValue["filterRangePeriod"]) => {
    setSelectedMultiValues((prev) => ({ ...prev, filterRangePeriod }));
  };

  const handleSelectFilterTYpe = (filterType: MultiValue["filterKeyType"]) => {
    setSelectedMultiValues((prev) => ({ ...prev, filterKeyType: filterType }));
  };

  const safeCostCenters = costCenters?.map((costCenter: CostCenter) => costCenter.uuid) || [];

  const items = selectedMultiValues.items;
  const { startDate, endDate } = selectedMultiValues.filterRangePeriod ?? {};

  const {
    costCenter,
    issuers,
    items: selectedItems,
    projects,
    phases,
    units,
  } = selectedMultiValues;

  const itemsToDebounce = useMemo(
    () => ({ costCenter, issuers, items: selectedItems, projects, phases, units }),
    [costCenter, issuers, selectedItems, projects, phases, units]
  );

  const [deboucedFilters] = useDebounce(itemsToDebounce, 1500);

  const costCentersFiltered = [...deboucedFilters.costCenter, ...safeCostCenters];

  const { data: dataByCostCenter, isFetching: isFetchingDataByCostCenter } = useQuery(
    [
      QueryKeys.BY_COST_CENTER,
      periodFrom,
      periodUntil,
      items,
      deboucedFilters,
      costCentersFiltered,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.byCostCenter,
        filter: {
          items,
          startDate,
          endDate,
          projects: deboucedFilters.projects,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          units: deboucedFilters.units,
          costCenters: costCentersFiltered,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: dataByUnit, isFetching: isFetchingDataByUnity } = useQuery(
    [
      QueryKeys.BY_UNITY,
      periodFrom,
      periodUntil,
      items,
      deboucedFilters,
      costCentersFiltered,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.byUnit,
        filter: {
          items,
          startDate,
          endDate,
          projects: deboucedFilters.projects,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          units: deboucedFilters.units,
          costCenters: costCentersFiltered,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: lastPurchases, isFetching: isFetchingLastPurchase } = useQuery(
    [
      QueryKeys.LAST_PURCHASE,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      deboucedFilters,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.lastPurchases,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: byProjects, isFetching: isFetchingByProjects } = useQuery(
    [
      QueryKeys.BY_PROJECT,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      deboucedFilters,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.byProject,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: byPhase, isFetching: isFetchingByPhases } = useQuery(
    [
      QueryKeys.BY_PHASE,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      deboucedFilters,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.byPhase,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          projects: deboucedFilters.projects,
          byPhases: deboucedFilters.phases,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: totalSpent, isFetching: isFetchingTotalSpent } = useQuery(
    [
      QueryKeys.TOTAL_SPENT,
      customerId,
      periodFrom,
      periodUntil,
      deboucedFilters,
      costCentersFiltered,
      selectedMultiValues.filterKeyType,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.totalSpent,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          projects: deboucedFilters.projects,
          byPhases: deboucedFilters.phases,
          filterKeyType: selectedMultiValues.filterKeyType,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: airwayItem, isFetching: isFetchingAirwayItem } = useQuery(
    [
      QueryKeys.AIRWAY_ITEM,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findItem({
        customerId: customerId,
        itemType: "airway",
        item: defaultByItemQuery,
        byItem: isByItem.airway,
        filter: {
          costCenters: costCentersFiltered,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          items,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: hotelItem, isFetching: isFetchingHotelItem } = useQuery(
    [
      QueryKeys.HOTEL_ITEM,
      deboucedFilters,
      customerId,
      costCentersFiltered,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findItem({
        customerId,
        itemType: "hotel",
        byItem: isByItem.hotel,
        item: defaultByItemQuery,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: roadItem, isFetching: isFetchingRoadItem } = useQuery(
    [
      QueryKeys.ROAD_ITEM,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findItem({
        customerId,
        itemType: "road",
        item: defaultByItemQuery,
        byItem: isByItem.road,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: vehicleItem, isFetching: isFetchingVehicleItem } = useQuery(
    [
      QueryKeys.VEHICLE_ITEM,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findItem({
        customerId,
        itemType: "vehicle",
        item: defaultByItemQuery,
        byItem: isByItem.vehicle,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          projects: deboucedFilters.projects,
          byPhases: deboucedFilters.phases,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: dataByIssuer, isFetching: isFetchingByIssuer } = useQuery(
    [
      QueryKeys.BY_ISSUER,
      customerId,
      periodFrom,
      periodUntil,
      items,
      deboucedFilters,
      costCentersFiltered,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId,
        query: CustomerBiQuery.byIssuer,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: leadTime, isFetching: isFetchingLeadTime } = useQuery(
    [
      QueryKeys.LEAD_TIME,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId,
        query: CustomerBiQuery.byLeadTIme,
        filter: {
          costCenters: costCentersFiltered,
          items,
          issuers: deboucedFilters.issuers,
          projects: deboucedFilters.projects,
          byPhases: deboucedFilters.phases,
          units: deboucedFilters.units,
          startDate: startDate,
          endDate: endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: costCenterData, isFetching: isFetchingCostCenter } = useQuery(
    [
      QueryKeys.CUSTOMER_COST_CENTERS,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      deboucedFiltersName,
    ],
    async () =>
      await costCenterService.find({
        customerId,
        hasIssuedOrder: true,
        name: deboucedFiltersName,
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: topRoutes, isFetching: isFetchingTopRoutes } = useQuery(
    [
      QueryKeys.TOP_ROUTES,
      deboucedFilters,
      costCentersFiltered,
      customerId,
      periodFrom,
      periodUntil,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.topRoutes,
        filter: {
          items,
          issuers: deboucedFilters.issuers,
          byPhases: deboucedFilters.phases,
          costCenters: costCentersFiltered,
          projects: deboucedFilters.projects,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const { data: routeAnalysis, isFetching: isFetchingRouteAnalysis } = useQuery(
    [
      QueryKeys.ROUTE_ANALYSIS,
      deboucedFilters,
      customerId,
      periodFrom,
      periodUntil,
      costCentersFiltered,
      items,
      startDate,
      endDate,
    ],
    async () =>
      await customerBiService.findBi({
        customerId: customerId,
        query: CustomerBiQuery.routeAnalysis,
        filter: {
          costCenters: costCentersFiltered,
          items,
          projects: deboucedFilters.projects,
          byPhases: deboucedFilters.phases,
          issuers: deboucedFilters.issuers,
          units: deboucedFilters.units,
          startDate,
          endDate,
        },
      }),
    {
      staleTime: QueryTimes.NORMAL,
      cacheTime: QueryTimes.NORMAL,
      enabled: !!customerId,
      refetchOnMount: "always",
      onError: (error) => {
        logError({
          logTag: LOG_TAG,
          error,
          defaultErrorMessage: SNACKBAR_MESSAGES.LOAD_ERROR_MESSAGE,
        });
      },
    }
  );

  const dataByItem = useMemo(() => {
    const items = [];
    if (airwayItem) items.push(...airwayItem);
    if (hotelItem) items.push(...hotelItem);
    if (roadItem) items.push(...roadItem);
    if (vehicleItem) items.push(...vehicleItem);
    return items;
  }, [airwayItem, hotelItem, roadItem, vehicleItem]);

  const dashboard = {
    byCostCenter: dataByCostCenter,
    byIssuer: dataByIssuer,
    byUnit: dataByUnit,
    topRoutes,
    dataByItem,
    lastPurchases,
    leadTime,
    routeAnalysis,
    totalSpent,
    costCenterData,
    byPhase,
    byProjects,
  };

  const isLoading = {
    costCenter: isFetchingDataByCostCenter,
    lastPurchase: isFetchingLastPurchase,
    airwayItem: isFetchingAirwayItem,
    hotelItem: isFetchingHotelItem,
    roadItem: isFetchingRoadItem,
    vehicleItem: isFetchingVehicleItem,
    byIssuer: isFetchingByIssuer,
    leadTime: isFetchingLeadTime,
    topRoutes: isFetchingTopRoutes,
    routeAnalysis: isFetchingRouteAnalysis,
    costCenterList: isFetchingCostCenter,
    totalSpent: isFetchingTotalSpent,
    isLoadingProjects: isFetchingByProjects,
    isFetchingByPhases: isFetchingByPhases,
    byUnit: isFetchingDataByUnity,
    dataByItem: [
      isFetchingAirwayItem,
      isFetchingHotelItem,
      isFetchingRoadItem,
      isFetchingVehicleItem,
    ].every((isFetching) => isFetching),
  };

  const onSearchCostCenter = useCallback((name: string) => setCostCenterName(name), []);

  const onChangeFilter = useCallback(
    (data: FilterCustomerBI) => {
      filterData.setValue("periodFrom", data.periodFrom);
      filterData.setValue("periodUntil", data.periodUntil);
      filterData.setValue("costCenters", data.costCenters);
      setModalPortalIsOpen(false);
    },
    [filterData]
  );
  const props = {
    dashboard: dashboard as CustomerBI,
    filterData,
    activeFilter,
    selectedMultiValues,
    isLoading,
    modalPrtalIsOpen,
    onChangeFilter,
    onSelectFilterType: handleSelectFilterTYpe,
    onSelectPeriodDate: handleSelectPeriodDate,
    onRemoveItem,
    onSetActiveValue: handleSetActiveFilter,
    onSearchCostCenter,
    onCloseModal: setModalPortalIsOpen,
    handleSelectStartDate,
    onSelectMultiValues: handleSelectMultiValues,
  };

  return <CustomerBiContaner {...props} />;
}
